/**
 * UTM tracking – capture from URL and pass to APIs.
 * All UTM sources persist for 6 hours so SPA navigations (home → pooja → book)
 * keep attribution even when the query string is dropped.
 */

import { slugifyForApi } from './pooja';

const UTM_STORAGE_KEY = 'daiv_utm';
/** Mirrors useTrackVisit – first-touch source for funnel */
const SOURCE_STORAGE_KEY = 'daiv_source';
/** How long stored UTM may be reused when the URL has no utm_source. */
const UTM_TTL_MS = 6 * 60 * 60 * 1000;

/** Read query param when the key may be malformed (e.g. `?%20utm_source=` → key is ` utm_source`). */
function getSearchParam(params, key) {
  const direct = params.get(key);
  if (direct != null && direct !== '') return direct;
  for (const [k, v] of params.entries()) {
    if (k.trim() === key) return v ?? '';
  }
  return '';
}

function persistUtm(payload) {
  const record = {
    utm_source: payload.utm_source || '',
    utm_medium: payload.utm_medium || '',
    utm_campaign: payload.utm_campaign || '',
    capturedAt: Date.now(),
  };
  const str = JSON.stringify(record);
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, str);
  } catch {
    // ignore
  }
  try {
    localStorage.setItem(UTM_STORAGE_KEY, str);
  } catch {
    // ignore
  }
  if (record.utm_source) {
    try {
      if (!localStorage.getItem(SOURCE_STORAGE_KEY)) {
        localStorage.setItem(SOURCE_STORAGE_KEY, record.utm_source);
      }
    } catch {
      // ignore
    }
  }
}

/** Meta/WhatsApp campaign names often use underscores; pooja URLs use hyphens. */
export function campaignNameToPoojaSlug(campaign) {
  const raw = String(campaign || '').trim();
  if (!raw) return '';
  return slugifyForApi(raw.replace(/_/g, '-'));
}

/**
 * Candidate slug from `utm_campaign` (not verified as a real pooja).
 * Website campaigns use marketing names (e.g. `regular`); pooja ads use real slugs.
 */
export function getCampaignPoojaSlugFromSearch(search = '') {
  const query = String(search || '').trim();
  if (!query) return '';
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  const campaign = (getSearchParam(params, 'utm_campaign') || '').trim();
  if (!campaign) return '';
  const slug = campaignNameToPoojaSlug(campaign);
  if (!slug || slug === 'pooja') return '';
  return slug;
}

/**
 * Unverified candidate path for `utm_campaign`. Prefer
 * `resolveVerifiedCampaignPoojaLandingPath` before navigating.
 */
export function getPoojaLandingPathFromSearch(search = '') {
  const slug = getCampaignPoojaSlugFromSearch(search);
  if (!slug) return null;
  return `/pooja/${slug}`;
}

/**
 * Redirect home → pooja only when `utm_campaign` matches a real LIVE/UPCOMING pooja.
 * Website-only campaigns (e.g. `utm_campaign=regular`) stay on `/` with UTMs tracked.
 */
export async function resolveVerifiedCampaignPoojaLandingPath(search = '') {
  const slug = getCampaignPoojaSlugFromSearch(search);
  if (!slug) return null;
  const { getPoojaDetailsBySlug } = await import('./pooja');
  for (const scheduleType of ['LIVE', 'UPCOMING']) {
    try {
      const pooja = await getPoojaDetailsBySlug(slug, scheduleType, 'en');
      if (pooja && (pooja.id || pooja.poojaDetailsId || pooja.slug)) {
        return `/pooja/${slug}`;
      }
    } catch {
      // try next schedule / stay on home if none match
    }
  }
  return null;
}

/**
 * Paid / cold-traffic landings (Meta, QR, Google Ads, click ids).
 * Used to slim above-fold UX and defer below-fold media for bounce/LCP.
 * Website UTMs like `utm_campaign=regular` without a paid source/medium stay false.
 */
const PAID_UTM_SOURCES = new Set([
  'meta',
  'facebook',
  'fb',
  'ig',
  'instagram',
  'whatsapp',
  'qr',
  'qrcode',
  'newspaper',
  'print',
  'cubera',
  'google',
  'googleads',
  'adwords',
  'youtube',
  'yt',
  'pioneer',
]);

const PAID_UTM_MEDIUMS = new Set([
  'cpc',
  'cpm',
  'ppc',
  'paid',
  'paid_social',
  'paid-social',
  'paidsocial',
  'display',
  'ads',
  'social',
  'qr',
  'qrcode',
  'print',
]);

export function isPaidCampaignLanding(search) {
  if (typeof window === 'undefined' && (search == null || search === '')) return false;
  const merged = getMergedUtmParams(
    search != null && search !== ''
      ? search
      : typeof window !== 'undefined'
        ? window.location?.search || ''
        : '',
  );
  const source = String(merged.utm_source || '').trim().toLowerCase();
  const medium = String(merged.utm_medium || '').trim().toLowerCase();
  if (source && PAID_UTM_SOURCES.has(source)) return true;
  if (medium && PAID_UTM_MEDIUMS.has(medium)) return true;

  const trimmed = String(search ?? (typeof window !== 'undefined' ? window.location?.search || '' : '')).trim();
  if (!trimmed) return false;
  const q = trimmed.startsWith('?') ? trimmed.slice(1) : trimmed;
  const params = new URLSearchParams(q);
  const clickId =
    getSearchParam(params, 'fbclid') ||
    getSearchParam(params, 'gclid') ||
    getSearchParam(params, 'msclkid') ||
    getSearchParam(params, 'click_id') ||
    getSearchParam(params, 'utm_click');
  return Boolean(String(clickId || '').trim());
}

/** Home / poojas promo popups — hide for paid / ad UTM landings (meta, ig, facebook, pioneer, …). */
export function shouldSuppressPromoPopup(search) {
  return isPaidCampaignLanding(search);
}

/**
 * Blind home→pooja rewrite is disabled (broke website UTMs).
 * Verified deep-links run async in App (`UtmCampaignLandingRedirect`).
 */
export function isCampaignPoojaHomeLanding(_pathname = '', _search = '') {
  return false;
}

/**
 * No-op: do not rewrite `/?utm_campaign=…` before verifying the pooja exists.
 * @returns {boolean} always false
 */
export function applyCampaignPoojaLandingFastPath() {
  return false;
}

/**
 * Capture UTM params from current URL and store (localStorage + sessionStorage).
 * Call synchronously on bootstrap and on route change when URL has utm params.
 * All sources (newspaper QR, WhatsApp, Meta, …) persist for UTM_TTL_MS.
 */
export function captureUtmFromUrl(search = '') {
  if (typeof window === 'undefined') return;
  const query = search || (window.location?.search || '');
  if (!query) return;
  const params = new URLSearchParams(query);
  const utm_source = getSearchParam(params, 'utm_source') || '';
  const utm_medium = getSearchParam(params, 'utm_medium') || '';
  const utm_campaign = getSearchParam(params, 'utm_campaign') || '';
  if (utm_source || utm_medium || utm_campaign) {
    persistUtm({ utm_source, utm_medium, utm_campaign });
  }
}

/**
 * WhatsApp autopay link must include all three: ?tkn=, utm_source=whatsapp, package_num= (1-based tier).
 */
export function isWhatsAppAutopayBookUrl(search = '') {
  const query = String(search || '').trim();
  if (!query) return false;
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  const tkn = (getSearchParam(params, 'tkn') || getSearchParam(params, 'token') || '').trim();
  if (!tkn) return false;
  const utmSource = (getSearchParam(params, 'utm_source') || '').trim().toLowerCase();
  if (utmSource !== 'whatsapp') return false;
  const packageNum = (getSearchParam(params, 'package_num') || '').trim();
  if (!packageNum) return false;
  const n = Number(packageNum);
  return Number.isFinite(n) && n >= 1;
}

/** Persist UTM from URL when token link is opened (before ?tkn= is stripped). */
export function captureWhatsAppUtmForTokenLink(search = '') {
  if (typeof window === 'undefined') return;
  const query = search || (window.location?.search || '');
  if (!query) return;
  const params = new URLSearchParams(query);
  const tkn = (getSearchParam(params, 'tkn') || getSearchParam(params, 'token') || '').trim();
  if (!tkn) return;
  captureUtmFromUrl(query);
}

/**
 * Stored UTMs merged with optional query string (URL wins when present — e.g. booking page `?utm_source=…`).
 * @param {string} [search] `location.search` or `?utm_…` string; omit to use storage only.
 */
export function getMergedUtmParams(search) {
  const stored = getUtmParams();
  if (!search || typeof search !== 'string') return stored;
  const trimmed = search.trim();
  if (!trimmed) return stored;
  const q = trimmed.startsWith('?') ? trimmed.slice(1) : trimmed;
  const params = new URLSearchParams(q);
  return {
    utm_source: getSearchParam(params, 'utm_source') || stored.utm_source || '',
    utm_medium: getSearchParam(params, 'utm_medium') || stored.utm_medium || '',
    utm_campaign: getSearchParam(params, 'utm_campaign') || stored.utm_campaign || '',
  };
}

/**
 * Clear stored campaign attribution after a successful payment so the next visit/booking
 * is not falsely attributed to the previous campaign.
 */
export function clearStoredUtm() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(SOURCE_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Get stored UTM params for API requests (localStorage first – survives tab / navigation).
 * Returns object with utm_source, utm_medium, utm_campaign (empty string if not set / expired).
 * Valid for UTM_TTL_MS (6 hours) for all campaign sources.
 */
export function getUtmParams() {
  if (typeof window === 'undefined') return {};
  const empty = { utm_source: '', utm_medium: '', utm_campaign: '' };
  const read = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      const capturedAt = Number(parsed.capturedAt);
      // Legacy entries without capturedAt (or invalid) → treat as expired to stop fake attribution.
      if (!Number.isFinite(capturedAt) || capturedAt <= 0) {
        clearStoredUtm();
        return null;
      }
      if (Date.now() - capturedAt > UTM_TTL_MS) {
        clearStoredUtm();
        return null;
      }
      return {
        utm_source: parsed.utm_source || '',
        utm_medium: parsed.utm_medium || '',
        utm_campaign: parsed.utm_campaign || '',
      };
    } catch {
      return null;
    }
  };
  try {
    const fromLocal = read(localStorage.getItem(UTM_STORAGE_KEY));
    if (fromLocal && (fromLocal.utm_source || fromLocal.utm_medium || fromLocal.utm_campaign)) {
      return fromLocal;
    }
  } catch {
    // ignore
  }
  try {
    const fromSession = read(sessionStorage.getItem(UTM_STORAGE_KEY));
    if (fromSession) return fromSession;
  } catch {
    // ignore
  }
  return empty;
}

/**
 * Merge UTM params into an API request body (only non-empty values).
 * @param {object} body
 * @param {string} [search] Pass `location.search` so UTMs on the current URL are sent even if storage is empty.
 */
export function withUtmParams(body, search) {
  const utm = getMergedUtmParams(search);
  const extra = {};
  if (utm.utm_source) extra.utm_source = utm.utm_source;
  if (utm.utm_medium) extra.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) extra.utm_campaign = utm.utm_campaign;
  return Object.keys(extra).length > 0 ? { ...body, ...extra } : body;
}

/**
 * Same as URL + storage merge, but only `utm_source` — for booking initiate (medium/campaign not sent).
 * @param {string} [search] `location.search`
 */
export function withUtmSourceOnly(body, search) {
  const merged = getMergedUtmParams(search);
  if (!merged.utm_source) return body;
  return { ...body, utm_source: merged.utm_source };
}

/**
 * Attribution + timing for `booking_details` on POST /api/poojabookings/initiate.
 * Merged UTM + URL params (campaign_id, click_id, gclid, fbclid, …).
 */
export function getBookingInitiateAttributionDetails(search) {
  const merged = getMergedUtmParams(search);
  const trimmed = String(search || '').trim();
  const q = trimmed.startsWith('?') ? trimmed.slice(1) : trimmed;
  const params = new URLSearchParams(q);
  const campaign_id =
    getSearchParam(params, 'campaign_id') ||
    getSearchParam(params, 'utm_id') ||
    getSearchParam(params, 'utm_campaign') ||
    merged.utm_campaign ||
    '';
  const click_id =
    getSearchParam(params, 'click_id') ||
    getSearchParam(params, 'utm_click') ||
    getSearchParam(params, 'gclid') ||
    getSearchParam(params, 'fbclid') ||
    getSearchParam(params, 'msclkid') ||
    '';
  const utmSource = String(merged.utm_source || '').trim();
  const out = {
    created_at: new Date().toISOString(),
    campaign_id: campaign_id || '',
    click_id: click_id || '',
  };
  // Only send utm_source when it actually exists — never invent "cubera".
  if (utmSource) out.utm_source = utmSource;
  return out;
}

/** Query keys to keep on post-booking URLs (thank-you, order-confirmed, invoice). Lang is not stored in URL. */
const MARKETING_CONTINUATION_KEYS = [
  'booking', // legacy thank-you ?booking= shared links
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_click', // Cubera template click id
  'click_id',
  'campaign',
  'campaign_id',
  'gclid',
  'fbclid',
  'msclkid',
  'coupon',
  'couponCode',
  'coupon_code',
  'promo',
  'promoCode',
  'promo_code',
];

/**
 * Builds `?utm_…` for navigations after payment (campaign tags). Language is not added — use localStorage / context.
 * @returns {string} e.g. `?utm_source=google` or `''`
 */
export function buildMarketingContinuationSearch(currentSearch, _langCode) {
  void _langCode;
  const incoming = new URLSearchParams(currentSearch || '');
  const params = new URLSearchParams();
  for (const key of MARKETING_CONTINUATION_KEYS) {
    const v = getSearchParam(incoming, key);
    if (v != null && v !== '') params.set(key, v);
  }
  const utm = getUtmParams();
  if (utm.utm_source && !params.get('utm_source')) params.set('utm_source', utm.utm_source);
  if (utm.utm_medium && !params.get('utm_medium')) params.set('utm_medium', utm.utm_medium);
  if (utm.utm_campaign && !params.get('utm_campaign')) params.set('utm_campaign', utm.utm_campaign);
  const s = params.toString();
  return s ? `?${s}` : '';
}

/**
 * @deprecated Booking id now lives in `/thank-you/:slug/:bookingId`. Kept for any external callers.
 */
export function buildPostBookingSearch(currentSearch, langCode, bookingId) {
  const base = buildMarketingContinuationSearch(currentSearch, langCode);
  const params = new URLSearchParams(base.startsWith('?') ? base.slice(1) : '');
  if (bookingId) params.set('booking', bookingId);
  const s = params.toString();
  return s ? `?${s}` : '';
}
